import { useEffect, useState, useRef } from "react";
import mapboxgl, { Map, Marker, LngLatLike, MapMouseEvent } from "mapbox-gl";
import { createRoot } from "react-dom/client";
import { Box, Button, Stack, Typography, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Select, MenuItem, TextField, IconButton } from "@mui/material";
import { CheckCircle, DeleteOutline, Place } from "@mui/icons-material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { MapType } from "../../../../hooks/map.type";
import { mockURL } from "../../../../utils/mock";
import { calculateRotatedCoordinates, loadOverlay } from "../../../../components/RobotMap/helpers/map";
import { VIEW } from "../../../../utils/breakpoint";
import { smoothTransition } from "../../../../utils/animate";
import DoorPointMarker from "../../../../components/RobotMap/_components/marker/door_point";
import LiftPointMarker from "../../../../components/RobotMap/_components/marker/lift_point";
import PinPointMarker from "../../../../components/RobotMap/_components/marker/pin_point";
import DoorIcon from "../../../../components/RobotMap/_icons/door";
import LiftIcon from "../../../../components/RobotMap/_icons/lift";
import OverlaySetting from "../../../../components/RobotMap/models/OverlaySetting";
import { getMapLabel } from "../../../scheduledJob/_function/common";
import { COLOR } from "../../../../utils/colors";
import { smallStyle } from "../../../../components/RobotMap/_style";
import { useListMap } from "../../../../hooks/map";
import { useNavigate, useParams } from "react-router-dom";
import { useListBuilding, useListConfigMap } from "../../../../hooks/config-page";
import { showErrorToast, showSuccessToast, toDegree } from "../../../../utils/utils";
import { useConfigDetailWaypoint, useConfigTypeWaypoint, useCreateWaypointMutation, useUpdateWaypointMutation } from "../../../../hooks/config-page/waypoint";
import { enqueueSnackbar } from "notistack";
import { WaypointType } from "../../../../hooks/config-page/waypoint.type";
import { useQueryState } from "nuqs";
import PinPointSelector from "../../../../components/PinPointSelector";
import StoreManagerSelector from "../store-manager-selector";

export type PinPointMapType = {
  name?: string,
  coordinates: [number, number],
  type?: string
  // x: number,
  // y: number
}

export type MapPointDataType = {
  uuid: string,
  points: PinPointMapType[]
  isEditing: boolean,
}

export default function EditLocationPage({
  onSuccess
}: {
  onSuccess?: () => void
}) {
  const { id } = useParams<{ id: string }>();
  const { data: dataWaypoint } = useConfigDetailWaypoint({ uuid: id || '', enabled: !!id });

  const mapContainer = useRef<HTMLElement | string>("");
  const map = useRef<Map | null>(null);
  // const [building_uuid] = useQueryState('building_uuid', { clearOnDefault: true })
  const [buildingId, setBuildingId] = useState('')
  const [selectedMapId, setSelectedMapId] = useState('')
  const [findByQueryBuilding, setFindByQueryBuilding] = useState(false)

  const { data: dataConfigMap, refetch: refetchConfigMap } = useListConfigMap({
    building_uuid: buildingId || undefined,
    page_size: 100
  })

  const { data: dataTypeWaypoint } = useConfigTypeWaypoint()
  const listTypeWaypoints = dataTypeWaypoint?.types || []

  const [selectedMap, setSelectedMap] = useState<MapType>()
  const optionMaps = dataConfigMap?.data || []
  const listMaps = selectedMap ? [selectedMap] : []
  const [previousListMaps, setPreviousListMaps] = useState<MapType[]>([])
  const [mapPinPoints, setMapPinPoints] = useState<MapPointDataType[]>([])
  const [selectedMapIndex, setSelectedMapIndex] = useState(0)
  const [showWarningDialog, setShowWarningDialog] = useState(false)
  const [storeManagers, setStoreManagers] = useState<string[]>([])

  const { data: dataBuilding } = useListBuilding({
    page_size: 100
  })
  const buildings = dataBuilding?.data || []

  const locationMaps = dataWaypoint?.data?.location.map?.map((i) => i.map) || []
  const isExist = optionMaps.filter((item) => locationMaps.findIndex((locMap) => locMap === item.map_uuid) >= 0)

  useEffect(() => {
    if (dataWaypoint && buildings.length > 0) {
      const currentBuilding = buildings.find((building) => building.building_uuid === dataWaypoint.data.building.building_uuid)
      if (currentBuilding) {
        setBuildingId(currentBuilding.building_uuid)
        setFindByQueryBuilding(true)
      }
    }

    if (dataWaypoint) {
      setStoreManagers(dataWaypoint.data.location.store_manager_id || [])
    }
  }, [dataWaypoint, buildings])

  useEffect(() => {
    refetchConfigMap()
  }, [buildingId]);

  useEffect(() => {
    if (optionMaps.length === 0) {
      return
    }

    const selectedMaps = optionMaps.filter((item) => locationMaps.findIndex((locMap) => locMap === item.map_uuid) >= 0)
    if (selectedMaps.length > 0) {
      setSelectedMap(selectedMaps[0])
    }

  }, [optionMaps, findByQueryBuilding]);

  const navigate = useNavigate()

  const mapPinPointsRef = useRef<MapPointDataType[]>(mapPinPoints)
  mapPinPointsRef.current = mapPinPoints

  useEffect(() => {
    if (listMaps.length === 0) {
      return
    }
    const tmpData: MapPointDataType[] = listMaps.map((item) => {
      return {
        uuid: item.map_uuid,
        points: [],
        isEditing: true
      }
    })
    setMapPinPoints(tmpData)

    if (!map.current) {
      return; // initialize map only once
    }
    if (previousListMaps.length > 0) {
      previousListMaps.forEach((item, index) => {
        try {
          map.current?.removeLayer(item.map_uuid)
          map.current?.removeSource(item.map_uuid)
        } catch (error) {

        }
        // map.current?.setLayoutProperty(item.map_uuid, "visibility", item.map_uuid !== selectedMapId ? "none" : "visible");
      })
    }
    loadMaps()
    setPreviousListMaps(listMaps)
  }, [selectedMap])

  useEffect(() => {
    if (!map.current) return; // initialize map only once
    if (!mapContainer.current) return
    if (mapPinPoints.length === 0) {
      return
    }
    if (map.current.isStyleLoaded()) {
      loadMaps()
      return
    }
    map.current.on('style.load', () => {
      loadMaps()
    })
  }, [mapPinPoints]);

  const onHandleClickPin = (e: MapMouseEvent) => {
    if (mapPinPointsRef.current.length > 0) {
      return
    }
    const tmpData = [...mapPinPointsRef.current]

    const currentMapIndex = currentMapIndexRef.current
    const currentMap = tmpData[currentMapIndex]
    if (!currentMap || !currentMap.isEditing) {
      return
    }
    const newData: PinPointMapType = {
      coordinates: [e.lngLat.lng, e.lngLat.lat],
      name: '',
      type: ''
    }
    currentMap.points.push(newData)
    tmpData[currentMapIndex] = currentMap

    setMapPinPoints(tmpData)
  }

  useEffect(() => {
    if (!dataWaypoint || !map.current) {
      return
    }

    setTimeout(() => {
      const location = dataWaypoint.data.location
      const lat = location.pose.latitude
      const newData: PinPointMapType = {
        coordinates: lat > 90 || lat < -90 ? [location.pose.latitude, location.pose.longitude] : [location.pose.longitude, location.pose.latitude],
        name: location.location_name,
        type: location.location_type
      }

      const newMapPinPoints: MapPointDataType = {
        uuid: dataWaypoint.data.map.map_uuid,
        points: [newData],
        isEditing: true,
      }

      setMapPinPoints([newMapPinPoints])
      setSelectedMapIndex(0)
    }, 2000);
  }, [dataWaypoint])

  const handleClickRef = useRef(onHandleClickPin)
  const currentMapIndexRef = useRef(selectedMapIndex)
  handleClickRef.current = onHandleClickPin

  const { trigger: submitUpdateWaypoint } = useUpdateWaypointMutation({
    onSuccess: (data) => {
      showSuccessToast('Waypoint updated successfully')
      if (onSuccess) {
        onSuccess()
        return
      }
      navigate("/config/locations")
    }
  })

  useEffect(() => {
    currentMapIndexRef.current = selectedMapIndex // update reference with every render
  }, [selectedMapIndex])

  useEffect(() => {
    if (!map.current || mapPinPoints.length === 0 || !mapPinPoints[selectedMapIndex] || mapPinPoints[selectedMapIndex].points.length === 0) return;
    const mapboxMap = map.current as Map;
    const markers: Marker[] = [];

    mapPinPoints[selectedMapIndex].points.map((item, index) => {
      const el = document.createElement("div");
      let element = undefined;
      el.classList.add("marker");
      const name = "marker-" + index
      el.classList.add(name);

      const pointProps = {
        name: `${item.name || 'Point ' + (index + 1)}`,
        rotation: 0
      }

      element = <PinPointMarker type={item.type} {...pointProps} />

      if (element) {
        createRoot(el).render(element);
      }
      const marker = new Marker(el, { draggable: true }).setLngLat(item.coordinates).addTo(mapboxMap)
      markers.push(marker);

      marker.on('dragend', (e) => {
        const tmpData = [...mapPinPoints]
        const currentPoint = tmpData[selectedMapIndex].points[index]
        currentPoint.coordinates = [marker.getLngLat().lng, marker.getLngLat().lat]

        setMapPinPoints(tmpData)
      })
    })

    return () => {
      markers.forEach((marker) => marker.remove());
    }
  }, [selectedMapIndex, mapPinPoints, map.current, mapPinPoints[selectedMapIndex]?.points])

  const setNameValue = (value: string, pointIndex: number) => {
    const tmpData = [...mapPinPoints]
    if (!tmpData[selectedMapIndex] || tmpData[selectedMapIndex].points.length === 0 || pointIndex >= tmpData[selectedMapIndex].points.length) {
      return
    }

    tmpData[selectedMapIndex].points[pointIndex].name = value
    setMapPinPoints(tmpData)
  }

  const setTypeValue = (value: string, pointIndex: number) => {
    const tmpData = [...mapPinPoints]
    if (!tmpData[selectedMapIndex] || tmpData[selectedMapIndex].points.length === 0 || pointIndex >= tmpData[selectedMapIndex].points.length) {
      return
    }

    tmpData[selectedMapIndex].points[pointIndex].type = value
    setMapPinPoints(tmpData)
  }

  const onSavePoints = () => {
    if (mapPinPoints[selectedMapIndex].points.length === 0) {
      return
    }

    const tmpData = [...mapPinPoints]
    tmpData[selectedMapIndex].isEditing = false
    setMapPinPoints(tmpData)
  }

  const onEditPoints = () => {
    const tmpData = [...mapPinPoints]
    tmpData[selectedMapIndex].isEditing = true
    setMapPinPoints(tmpData)
  }

  const isFullForm = (index: number) => {
    if (mapPinPoints.length === 0 || mapPinPoints.length <= index || !mapPinPoints[index] || mapPinPoints[index].points.length === 0 || mapPinPoints[index].isEditing) {
      return false
    }

    const filledAll = mapPinPoints[index].points.filter((item) => !!item.name && !!item.type).length === mapPinPoints[index].points.length

    if (!filledAll) {
      return false
    }

    // return !mapPinPoints[index].isEditing
    return true
  }


  const getIcon = (type: string) => {
    return <PinPointSelector type={type} />;
  }

  const loadMaps = () => {
    if (!map || !map.current || !mapContainer.current || listMaps.length === 0) {
      return
    }


    listMaps.forEach((item, index) => {
      const degreeRotation = toDegree(item?.map_orientation || 0)

      const mapOverlayData: OverlaySetting = {
        uuid: item.map_uuid,
        url: mockURL(item.map_image),
        topLeftLng: item.map_longitude_tl,
        topLeftLat: item.map_latitude_tl,
        widthInGIS: Math.abs(item.map_longitude_br - item.map_longitude_tl),
        heightInGIS: Math.abs(item.map_latitude_tl - item.map_latitude_br),
        opacity: 100,
        level: item.map_level,
        rotation: degreeRotation
      };

      loadOverlay(map, mapOverlayData, item.map_uuid)

      if (map.current?.isStyleLoaded()) {
        map.current?.setLayoutProperty(item.map_uuid, "visibility", index > 0 ? "none" : "visible");
      } else {
        map.current?.on('style.load', () => {
          map.current?.setLayoutProperty(item.map_uuid, "visibility", index > 0 ? "none" : "visible");
        });
      }
    })
    setSelectedMapId(listMaps[0].map_uuid)
  }

  useEffect(() => {
    if (!dataWaypoint) {
      return
    }
    if (map.current) return; // initialize map only once
    if (!mapContainer.current) return
    let coordinates: LngLatLike = [dataWaypoint.data.location.pose.longitude, dataWaypoint.data.location.pose.latitude];
    if (coordinates[1] > 90 || coordinates[1] < -90) {
      coordinates = [dataWaypoint.data.location.pose.latitude, dataWaypoint.data.location.pose.longitude];
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/hanscau/clxan4269025p01qxaxqc0ekr",
      center: coordinates,
      zoom: 18,
      minZoom: 10,
      maxZoom: 26,
    });

    map.current?.on('click', handleClickRef.current);
  }, [dataWaypoint]);

  useEffect(() => {
    if (!selectedMapId || listMaps.length === 0 || !map.current) {
      return
    }
    const findIndex = listMaps.findIndex((item) => item.map_uuid === selectedMapId)
    if (findIndex < 0) {
      return
    }
    // setSelectedMapIndex(findIndex)
    const selectedMap = listMaps[findIndex]
    setSelectedMapIndex(findIndex)
    listMaps.map((rMap) => {
      if (map.current?.isStyleLoaded()) {
        map.current?.setLayoutProperty(rMap.map_uuid, "visibility", (rMap.map_uuid === selectedMap.map_uuid) ? "visible" : 'none');
      }
    })
  }, [selectedMapId])

  const onSubmit = () => {
    if(!dataWaypoint || !id) {
      return
    }
    // navigate("/config/locations")
    if (mapPinPoints.length === 0 || mapPinPoints[currentMapIndexRef.current].points.length === 0) {
      showErrorToast('No waypoint data to submit')
      return
    }

    // Validate all form filled
    const checkedLists = mapPinPoints.filter((item, index) => isFullForm(index)).length === mapPinPoints.length
    if (!checkedLists) {
      showErrorToast('Please fill in all waypoint data before submitting')
      return
    }


    submitUpdateWaypoint({
      location_uuid: id || '',
      map_uuid: mapPinPoints[0].uuid,
      waypoints: mapPinPoints[currentMapIndexRef.current].points.map((item) => ({
        name: item.name || '',
        longitude: item.coordinates[0] as number,
        latitude: item.coordinates[1] as number,
        type: item.type === 'delivery_point' ? 'waypoint' : item.type as WaypointType
      })),
      store_manager_uuids: storeManagers
    })

  }

  const handleSubmit = () => {
    const checkedLists = mapPinPoints.filter((item, index) => isFullForm(index)).length === mapPinPoints.length
    if (!checkedLists) {
      setShowWarningDialog(true)
      return
    }
    onSubmit?.()
  }

  const handleBypassSubmit = () => {
    setShowWarningDialog(false)
    onSubmit?.()
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '100vw', width: '100%', mx: 'auto' }}>
        <Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
          <Box
            ref={mapContainer}
            sx={{
              position: 'absolute',
              inset: 0,
              width: "100%",
              height: "100%",
              // zIndex: 0,
            }}
          ></Box>
          <Box sx={{ position: 'absolute', bottom: '28px', right: '18px', display: 'flex', justifyContent: 'end', alignItems: 'center', mt: '46px', width: 'auto', backgroundColor: 'white', mx: 'auto' }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={mapPinPoints.length === 0 || mapPinPoints.filter((item) => item.isEditing).length > 0 || mapPinPoints.filter((item) => item.points.length === 0).length > 0}
              sx={{
                paddingInline: '78px',
                paddingTop: '8px',
                paddingBottom: '8px',
                backgroundColor: '#378FFE'
              }}>Submit</Button>
          </Box>
          {!!mapPinPoints && mapPinPoints.length > 0 && mapPinPoints[selectedMapIndex] && (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                [theme.breakpoints.up(VIEW.PHONE)]: {
                  right: '8px',
                  left: '8px',
                  top: 'auto',
                  bottom: '86px'
                },
                [theme.breakpoints.up(VIEW.TABLET)]: {
                  left: 'auto',
                  right: '16px',
                  top: '16px',
                  bottom: 'auto'
                },
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '10px'
              })}>
              <Accordion
                sx={(theme) => ({
                  [theme.breakpoints.up(VIEW.PHONE)]: {
                    width: '100%'
                  },
                  [theme.breakpoints.up(VIEW.TABLET)]: {
                    width: '400px'
                  },
                })} defaultExpanded>
                <AccordionSummary
                  expandIcon={<GridExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography component="span" fontWeight={600}>Waypoints</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '39vh', overflowX: 'auto', paddingBottom: '18px' }}>
                      {mapPinPoints[selectedMapIndex].points.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            {!mapPinPoints[selectedMapIndex].isEditing && getIcon(item.type || '')}
                            <Typography sx={{ flex: 'auto', fontSize: mapPinPoints[selectedMapIndex].isEditing ? '18px' : '16px', fontWeight: 500, color: COLOR.black }}>Point {index + 1} {item.name ? '(' + item.name + ')' : ''}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '11px', fontStyle: 'italic', color: COLOR.neutral[800] }}>
                            {`lng, lat: ${(item.coordinates as any)[0]}, ${(item.coordinates as any)[1]}`}
                          </Typography>
                          {index === 0 && mapPinPoints[selectedMapIndex].isEditing && (
                            <Typography sx={{ fontSize: '12px', mt: '8px', color: COLOR.neutral[800] }}>
                              Please enter the Waypoint data
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', mt: mapPinPoints[selectedMapIndex].isEditing ? '8px' : '2px', flexDirection: 'column', gap: mapPinPoints[selectedMapIndex].isEditing ? '12px' : '2px' }}>
                            {mapPinPoints[selectedMapIndex].isEditing && (
                              <>
                                <Box sx={{ display: 'flex', flex: mapPinPoints[selectedMapIndex].isEditing ? 'auto' : '', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                                  <Typography sx={{ width: '40px', fontSize: '12px', color: COLOR.neutral[800] }}>
                                    Name:
                                  </Typography>

                                  <TextField
                                    value={item.name || ''}
                                    inputProps={{
                                      step: "0.001"
                                    }}
                                    onChange={(e) => setNameValue(e.target.value, index)}
                                    sx={{ flex: 'auto' }}
                                    size="small"
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', flex: mapPinPoints[selectedMapIndex].isEditing ? 'auto' : '', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                                  <Typography sx={{ width: mapPinPoints[selectedMapIndex].isEditing ? '40px' : '100%', flex: 'none', fontSize: '12px', color: COLOR.neutral[800] }}>
                                    Type: {mapPinPoints[selectedMapIndex].isEditing ? '' : item.type}
                                  </Typography>
                                  <Select
                                    displayEmpty
                                    value={item.type || ''}
                                    placeholder="Select Point Type"
                                    size="small"
                                    sx={{ width: '100%', flex: 'auto' }}
                                    onChange={(e) => setTypeValue(e.target.value, index)}
                                  >
                                    {listTypeWaypoints.map((typeItem) => (
                                      <MenuItem key={typeItem.value} value={typeItem.value}>{typeItem.name}</MenuItem>
                                    ))}
                                  </Select>
                                </Box>
                              </>
                            )}
                          </Box>
                        </Box>
                      ))}

                      {mapPinPoints[selectedMapIndex].points.length === 0 && (
                        <Typography sx={{ ...smallStyle, textAlign: 'center', color: COLOR.gray[500] }}>Pin a point on the map to add waypoints</Typography>
                      )}
                    </Box>
                    {mapPinPoints[selectedMapIndex].points.length > 0 && mapPinPoints[selectedMapIndex].isEditing && (
                      <Box sx={{ width: '100%', mt: '8px' }}>
                        <Button onClick={() => onSavePoints()} variant="contained" sx={{ width: '100%' }}>SAVE</Button>
                      </Box>
                    )}

                    {!mapPinPoints[selectedMapIndex].isEditing && (
                      <Box sx={{ width: '100%', mt: '8px' }}>
                        <Button onClick={() => onEditPoints()} variant="outlined" sx={{ width: '100%' }}>EDIT</Button>
                      </Box>
                    )}
                  </>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}


          <Box sx={{ borderRadius: '8px', border: `1px solid ${COLOR.gray[300]}`, color: COLOR.black, backgroundColor: 'white', position: 'absolute', top: '16px', left: '16px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontFamily: "Poppins", fontSize: '18px', fontWeight: 600, color: COLOR.black }}>
                Add Waypoints
              </Typography>
              <Typography sx={{ fontFamily: "Poppins", fontSize: '14px', fontWeight: 400, color: COLOR.black, opacity: 0.6 }}>
                {!selectedMap ? 'Select Building and the Floor Map' : 'Pin a point on the map to add waypoints'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', }}>
              <Typography sx={{ width: '85px', flex: 'none' }}>Building: </Typography>
              <Select
                size='small'
                displayEmpty
                disabled={findByQueryBuilding}
                value={buildingId}
                onChange={(e) => setBuildingId(e.target.value)}
                sx={{ width: '100%', flex: 'auto', minWidth: '200px' }}
              >
                {buildings.map((item) => (
                  <MenuItem
                    key={item.building_uuid}
                    value={item.building_uuid}
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {item.building_name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', }}>
              <Typography sx={{ width: '85px', flex: 'none' }}>Floor Map: </Typography>
              <Select
                size='small'
                displayEmpty
                value={selectedMap?.map_uuid || ''}
                disabled={!buildingId}
                onChange={(e) => setSelectedMap(optionMaps.find((item) => item.map_uuid === e.target.value) || undefined)}
                sx={{ width: '100%', flex: 'auto', minWidth: '200px' }}
              >
                {optionMaps.map((item, index) => (
                  <MenuItem
                    key={index}
                    value={item.map_uuid}
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {item.map_name_display}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <StoreManagerSelector value={storeManagers} onChange={(e) => setStoreManagers(e.map((item) => item.uuid))} />
            
          </Box>

        </Box>
      </Box>
      <Dialog
        open={showWarningDialog}
        onClose={() => setShowWarningDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Are you sure want to skip this step?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            There is some maps that need to add a waypoints, are you sure want to skip?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWarningDialog(false)} autoFocus>No, Cancel</Button>
          <Button onClick={() => handleBypassSubmit()}>
            Yes, Sure
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}